import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  MapPin,
  DollarSign,
  TrendingUp,
  Calendar,
  Home,
  Edit,
  ArrowLeft,
  Download,
  Share2,
  Calculator,
  BarChart3,
  Building,
  Receipt
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useToastHelpers } from '../contexts/ToastContext'
import { apiClient, PropertyResponse } from '../services/api'
import { Button } from '../components/ui/Button'
import { NavigationHeader } from '../components/ui/NavigationHeader'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { AnalysisResultsChart } from '../components/ui/AnalysisResultsChart'
import { Tabs } from '../components/ui/Tabs'
import { ProfessionalMetrics } from '../components/ui/ProfessionalMetrics'
import { generatePropertyReportHTML, printHTMLReport } from '../utils/export'
import { getRenovationCosts, hasRenovationCosts, getRenovationCostEntries, safeNumber } from '../utils/propertyCalculations'
import {
  UserTaxProfile,
  AnnualTaxSummary,
  ScheduleEHelper,
  DepreciationScheduleVisualization,
  Exchange1031Calculator
} from '../components/TaxAnalysis'
import { UserTaxProfile as TaxProfileType } from '../types/tax-analysis'

interface PropertyDetailsProps {
  // This can be extended for future use
}

// Analysis result interface to match AnalysisResultsChart expectations
interface AnalysisResult {
  analysisType: 'buy-hold' | 'flip' | 'brrr';
  results: {
    // Buy & Hold Results
    monthlyRent?: number;
    monthlyExpenses?: number;
    monthlyCashFlow?: number;
    annualCashFlow?: number;
    cashOnCashReturn?: number;
    capRate?: number;
    totalCashInvested?: number;
    
    // Flip Results
    totalProjectCost?: number;
    expectedProfit?: number;
    profitMargin?: number;
    roi?: number;
    
    // BRRR Results
    totalInvestment?: number;
    refinanceAmount?: number;
    cashRecovered?: number;
    cashLeftIn?: number;
    infiniteReturn?: boolean;
  };
}

export function PropertyDetailsPage(_props: PropertyDetailsProps) {
  const { propertyId } = useParams<{ propertyId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showError, showSuccess } = useToastHelpers()
  const [property, setProperty] = useState<PropertyResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [taxProfile, setTaxProfile] = useState<TaxProfileType | null>(null)
  const [loadingTaxProfile, setLoadingTaxProfile] = useState(true)

  useEffect(() => {
    if (propertyId) {
      loadPropertyDetails()
      loadTaxProfile()
    }
  }, [propertyId])

  const loadTaxProfile = async () => {
    if (!user) return

    try {
      setLoadingTaxProfile(true)
      // TODO: Replace with actual API call when backend is ready
      // const profile = await apiClient.getTaxProfile(user.id)

      // For now, create a default/sample tax profile
      const defaultProfile: TaxProfileType = {
        user_id: user.id || 'temp',
        filing_status: 'married_joint',
        federal_tax_bracket: 0.22,
        state: 'CA',
        state_tax_rate: 0.133,
        depreciation_method: 'straight_line',
        material_participation: false,
        agi_estimate: 150000,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }

      setTaxProfile(defaultProfile)
    } catch (error) {
      console.error('Failed to load tax profile:', error)
      // Don't show error for tax profile - it's optional
    } finally {
      setLoadingTaxProfile(false)
    }
  }

  const loadPropertyDetails = async () => {
    if (!propertyId) {
      setError('Property ID is required')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const propertyData = await apiClient.getUserProperty(propertyId)
      setProperty(propertyData)
    } catch (err) {
      console.error('Failed to load property details:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load property details'
      setError(errorMessage)
      showError('Load Failed', errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    navigate(`/property/${propertyId}/edit`)
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      showSuccess('Link Copied', 'Property link copied to clipboard!')
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      showError('Share Failed', 'Failed to copy link to clipboard.')
    }
  }

  const handleDownload = () => {
    if (!property) {
      showError('Download Failed', 'No property data available to export.')
      return
    }

    try {
      const reportHTML = generatePropertyReportHTML(property)
      printHTMLReport(reportHTML)
      showSuccess('Report Generated', 'Property analysis report is opening in a new window.')
    } catch (error) {
      console.error('Export failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate property report. Please try again.'
      showError('Export Failed', errorMessage)
    }
  }

  // Calculate key metrics with safe numeric handling
  const calculateMetrics = (prop: PropertyResponse) => {
    const totalRenovationCosts = getRenovationCosts(prop.renovation_costs)
    const totalInvestment = safeNumber(prop.purchase_price) + totalRenovationCosts

    const monthlyMortgage = calculateMonthlyPayment(
      safeNumber(prop.loan_amount),
      safeNumber(prop.interest_rate),
      safeNumber(prop.loan_term)
    )

    // Use safeNumber for all arithmetic operations to prevent NaN
    const monthlyTaxes = safeNumber(prop.annual_taxes) / 12
    const monthlyInsurance = safeNumber(prop.annual_insurance) / 12
    const monthlyHoa = safeNumber(prop.monthly_hoa_fees)

    const monthlyExpenses = monthlyMortgage + monthlyTaxes + monthlyInsurance + monthlyHoa
    const expectedRent = safeNumber(prop.expected_monthly_rent)
    const monthlyCashFlow = expectedRent - monthlyExpenses
    const annualCashFlow = monthlyCashFlow * 12

    // Safe division with fallback for cash-on-cash return
    const downPayment = safeNumber(prop.down_payment)
    const cashOnCashReturn = downPayment > 0
      ? (annualCashFlow / downPayment) * 100
      : 0

    // Safe calculation for NOI and cap rate
    const annualRent = expectedRent * 12
    const annualTaxes = safeNumber(prop.annual_taxes)
    const annualInsurance = safeNumber(prop.annual_insurance)
    const noi = annualRent - annualTaxes - annualInsurance

    const purchasePrice = safeNumber(prop.purchase_price)
    const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0

    return {
      totalInvestment,
      totalRenovationCosts,
      monthlyMortgage,
      monthlyExpenses,
      monthlyCashFlow,
      annualCashFlow,
      cashOnCashReturn,
      capRate,
      noi
    }
  }

  const getAnalysisTypeColor = (status: string) => {
    switch (status) {
      case 'buy-hold':
        return 'bg-blue-100 text-blue-800'
      case 'flip':
        return 'bg-green-100 text-green-800'
      case 'brrr':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getAnalysisTypeLabel = (status: string) => {
    switch (status) {
      case 'buy-hold':
        return 'Buy & Hold'
      case 'flip':
        return 'Fix & Flip'
      case 'brrr':
        return 'BRRR'
      default:
        return 'Analysis'
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600">Please log in to view property details.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">Error Loading Property</div>
            <div className="text-gray-600 mb-4">{error || 'Property not found'}</div>
            <div className="space-x-4">
              <Button onClick={() => navigate('/portfolio')}>Back to Portfolio</Button>
              {propertyId && (
                <Button variant="secondary" onClick={loadPropertyDetails}>Try Again</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const metrics = calculateMetrics(property)

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/portfolio">
              <Button variant="secondary" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Portfolio
              </Button>
            </Link>
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{property.property_address}</h1>
              <div className="flex items-center text-gray-500 mt-1">
                <MapPin className="w-4 h-4 mr-1" />
                {property.city}, {property.state} {property.zip_code}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Button variant="secondary" size="sm" onClick={handleShare} className="flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button variant="secondary" size="sm" onClick={handleDownload} className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export Report
            </Button>
            <Button onClick={handleEdit} className="flex items-center gap-2">
              <Edit className="w-4 h-4" />
              Edit Property
            </Button>
          </div>
        </div>

        {/* Property Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Main Property Info */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Home className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Property Information</h2>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAnalysisTypeColor(property.analysis_status)}`}>
                        {getAnalysisTypeLabel(property.analysis_status)}
                      </span>
                      {property.property_type && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {property.property_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="w-4 h-4 mr-1" />
                  Added {new Date(property.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Property Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Price</label>
                    <p className="text-lg font-semibold text-gray-900">${property.purchase_price.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Down Payment</label>
                    <p className="text-lg font-semibold text-gray-900">${property.down_payment.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Loan Amount</label>
                    <p className="text-lg font-semibold text-gray-900">${property.loan_amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Interest Rate</label>
                    <p className="text-lg font-semibold text-gray-900">{property.interest_rate}%</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Annual Taxes</label>
                    <p className="text-lg font-semibold text-gray-900">${property.annual_taxes.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Annual Insurance</label>
                    <p className="text-lg font-semibold text-gray-900">${property.annual_insurance.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expected Monthly Rent</label>
                    <p className="text-lg font-semibold text-gray-900">${(property.expected_monthly_rent || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Loan Term</label>
                    <p className="text-lg font-semibold text-gray-900">{property.loan_term} years</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Metrics Sidebar */}
          <div className="space-y-6">
            {/* Cash Flow Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Cash Flow</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Monthly Rent:</span>
                  <span className="font-medium text-green-600">+${(property.expected_monthly_rent || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Mortgage Payment:</span>
                  <span className="font-medium text-red-600">-${metrics.monthlyMortgage.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Taxes & Insurance:</span>
                  <span className="font-medium text-red-600">-${Math.round((property.annual_taxes + property.annual_insurance) / 12).toLocaleString()}</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-medium text-gray-900">Net Cash Flow:</span>
                  <span className={`font-bold text-lg ${metrics.monthlyCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${metrics.monthlyCashFlow.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* ROI Metrics Card */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Investment Returns</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cash-on-Cash Return:</span>
                  <span className="font-semibold text-blue-600">{metrics.cashOnCashReturn.toFixed(2)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cap Rate:</span>
                  <span className="font-semibold text-purple-600">{metrics.capRate.toFixed(2)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Annual Cash Flow:</span>
                  <span className={`font-semibold ${metrics.annualCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${metrics.annualCashFlow.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Summary */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Investment Summary</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                      Total Investment
                    </p>
                    <p className="text-2xl font-bold text-blue-900">
                      ${metrics.totalInvestment.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600 uppercase tracking-wide">
                      Monthly Cash Flow
                    </p>
                    <p className={`text-2xl font-bold ${metrics.monthlyCashFlow >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                      ${metrics.monthlyCashFlow.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">
                      Cash-on-Cash ROI
                    </p>
                    <p className="text-2xl font-bold text-purple-900">
                      {metrics.cashOnCashReturn.toFixed(1)}%
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600 uppercase tracking-wide">
                      Cap Rate
                    </p>
                    <p className="text-2xl font-bold text-orange-900">
                      {metrics.capRate.toFixed(1)}%
                    </p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Content */}
        <Tabs
          tabs={[
            {
              id: 'overview',
              label: 'Overview',
              icon: <Home className="w-4 h-4" />,
              content: (
                <div className="space-y-6">
                  {/* Key Investment Metrics Overview */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Investment Metrics</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Cash-on-Cash Return</p>
                        <p className="text-2xl font-bold text-blue-900">{metrics.cashOnCashReturn.toFixed(1)}%</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-green-600 uppercase tracking-wide">Cap Rate</p>
                        <p className="text-2xl font-bold text-green-900">{metrics.capRate.toFixed(1)}%</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-purple-600 uppercase tracking-wide">Monthly Cash Flow</p>
                        <p className={`text-2xl font-bold ${metrics.monthlyCashFlow >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                          ${metrics.monthlyCashFlow.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Property Summary */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Purchase Price</p>
                        <p className="font-semibold">${property.purchase_price.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Down Payment</p>
                        <p className="font-semibold">${property.down_payment.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Monthly Rent</p>
                        <p className="font-semibold">${(property.expected_monthly_rent || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Total Investment</p>
                        <p className="font-semibold">${metrics.totalInvestment.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            },
            {
              id: 'charts',
              label: 'Analysis Charts',
              icon: <BarChart3 className="w-4 h-4" />,
              content: (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Analysis Charts & Projections</h3>
                    <AnalysisResultsChart result={convertPropertyToAnalysisResult(property)} />
                  </div>
                </div>
              )
            },
            {
              id: 'professional-metrics',
              label: 'Professional Metrics',
              icon: <Calculator className="w-4 h-4" />,
              content: <ProfessionalMetrics property={property} loading={loading} />
            },
            {
              id: 'tax-analysis',
              label: 'Tax Analysis',
              icon: <Receipt className="w-4 h-4" />,
              content: (
                <div className="space-y-6">
                  {/* Tax Profile Section */}
                  <UserTaxProfile
                    profile={taxProfile}
                    onProfileUpdate={setTaxProfile}
                    loading={loadingTaxProfile}
                  />

                  {/* Tax Analysis Tabs */}
                  <Tabs
                    tabs={[
                      {
                        id: 'tax-summary',
                        label: 'Annual Summary',
                        content: (
                          <AnnualTaxSummary
                            property={property}
                            taxProfile={taxProfile}
                            loading={loadingTaxProfile}
                          />
                        )
                      },
                      {
                        id: 'schedule-e',
                        label: 'Schedule E Helper',
                        content: (
                          <ScheduleEHelper
                            property={property}
                            loading={loading}
                          />
                        )
                      },
                      {
                        id: 'depreciation',
                        label: 'Depreciation Schedule',
                        content: (
                          <DepreciationScheduleVisualization
                            property={property}
                            taxProfile={taxProfile}
                            loading={loadingTaxProfile}
                          />
                        )
                      },
                      {
                        id: '1031-exchange',
                        label: '1031 Exchange',
                        content: (
                          <Exchange1031Calculator
                            property={property}
                            taxProfile={taxProfile}
                            loading={loadingTaxProfile}
                          />
                        )
                      }
                    ]}
                    defaultTab="tax-summary"
                    variant="pills"
                    size="sm"
                  />
                </div>
              )
            },
            ...(hasRenovationCosts(property.renovation_costs) ? [{
              id: 'renovations',
              label: 'Renovation Costs',
              icon: <Building className="w-4 h-4" />,
              badge: `$${metrics.totalRenovationCosts.toLocaleString()}`,
              content: (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Renovation Costs</h3>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {getRenovationCostEntries(property.renovation_costs).map(([category, cost]) => (
                        cost > 0 && (
                          <div key={category} className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm font-medium text-gray-700 capitalize mb-1">
                              {category.replace('_', ' ')}
                            </p>
                            <p className="text-lg font-semibold text-gray-900">
                              ${cost.toLocaleString()}
                            </p>
                          </div>
                        )
                      ))}
                      <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                        <p className="text-sm font-medium text-blue-700 mb-1">
                          Total Renovations
                        </p>
                        <p className="text-lg font-bold text-blue-900">
                          ${metrics.totalRenovationCosts.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            }] : [])
          ]}
          defaultTab="overview"
          variant="underline"
          size="md"
          className="mb-8"
        />
      </main>
    </div>
  )
}

// Helper function to calculate monthly mortgage payment
function calculateMonthlyPayment(loanAmount: number, annualInterestRate: number, loanTermYears: number): number {
  const monthlyInterestRate = annualInterestRate / 100 / 12
  const numberOfPayments = loanTermYears * 12
  
  if (monthlyInterestRate === 0) {
    return loanAmount / numberOfPayments
  }
  
  const monthlyPayment = loanAmount * 
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)
  
  return Math.round(monthlyPayment)
}

// Convert PropertyResponse to AnalysisResult format for charts with safe numeric handling
function convertPropertyToAnalysisResult(property: PropertyResponse): AnalysisResult {
  const totalRenovationCosts = getRenovationCosts(property.renovation_costs)
  const totalCashInvested = safeNumber(property.down_payment) + totalRenovationCosts

  const monthlyMortgage = calculateMonthlyPayment(
    safeNumber(property.loan_amount),
    safeNumber(property.interest_rate),
    safeNumber(property.loan_term)
  )

  // Use safeNumber for all arithmetic operations to prevent NaN
  const monthlyTaxes = safeNumber(property.annual_taxes) / 12
  const monthlyInsurance = safeNumber(property.annual_insurance) / 12
  const monthlyHoa = safeNumber(property.monthly_hoa_fees)
  const monthlyExpenses = monthlyMortgage + monthlyTaxes + monthlyInsurance + monthlyHoa

  const expectedRent = safeNumber(property.expected_monthly_rent)
  const monthlyCashFlow = expectedRent - monthlyExpenses
  const annualCashFlow = monthlyCashFlow * 12
  const cashOnCashReturn = totalCashInvested > 0
    ? (annualCashFlow / totalCashInvested) * 100
    : 0

  // Safe calculation for NOI and cap rate
  const annualRent = expectedRent * 12
  const annualTaxes = safeNumber(property.annual_taxes)
  const annualInsurance = safeNumber(property.annual_insurance)
  const noi = annualRent - annualTaxes - annualInsurance

  const purchasePrice = safeNumber(property.purchase_price)
  const capRate = purchasePrice > 0 ? (noi / purchasePrice) * 100 : 0

  // Determine analysis type from property.analysis_status
  const analysisType = property.analysis_status === 'flip' ? 'flip' : 
                      property.analysis_status === 'brrr' ? 'brrr' : 'buy-hold'

  const result: AnalysisResult = {
    analysisType: analysisType as 'buy-hold' | 'flip' | 'brrr',
    results: {
      monthlyRent: property.expected_monthly_rent || 0,
      monthlyExpenses,
      monthlyCashFlow,
      annualCashFlow,
      cashOnCashReturn,
      capRate,
      totalCashInvested,
    }
  }

  // Add analysis-specific results
  if (analysisType === 'flip') {
    const totalProjectCost = property.purchase_price + totalRenovationCosts + (property.selling_costs || 0)
    const expectedProfit = (property.after_repair_value || 0) - totalProjectCost
    const profitMargin = property.after_repair_value ? (expectedProfit / property.after_repair_value) * 100 : 0
    const roi = totalCashInvested > 0 ? (expectedProfit / totalCashInvested) * 100 : 0

    result.results = {
      ...result.results,
      totalProjectCost,
      expectedProfit,
      profitMargin,
      roi,
    }
  }

  if (analysisType === 'brrr') {
    const totalInvestment = totalCashInvested
    const refinanceAmount = (property.after_repair_value || 0) * 0.75 // Assume 75% LTV
    const cashRecovered = Math.min(refinanceAmount - property.loan_amount, totalInvestment)
    const cashLeftIn = totalInvestment - cashRecovered
    const infiniteReturn = cashLeftIn <= 0

    result.results = {
      ...result.results,
      totalInvestment,
      refinanceAmount,
      cashRecovered,
      cashLeftIn,
      infiniteReturn,
    }
  }

  return result
}